import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTheme } from '@tanium/react-theme-context';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import IDropdownOption from 'common/IDropdownOption';
import { TARGET_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import Tooltip from 'components/tooltip/Tooltip';
import MsEntraIDSelectors from 'components/pages/console/store/MsEntraIDSelectors';
import { MsEntraID } from 'gql/MsEntraID';
import MsEntraIDSlice from 'components/pages/console/store/MsEntraIDSlice';

export const MsEntraIDEnvironment = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();
  const msEntraIDs: MsEntraID[] = useSelector(MsEntraIDSelectors.selectMsEntraIDs);
  const loading = useSelector(MsEntraIDSelectors.selectMsEntraIDLoading);
  const [msEntraIDOptions, setMsEntraIDOptions] = useState<IDropdownOption[]>([]);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);

  useEffect(() => {
    dispatch(MsEntraIDSlice.actions.listMsEntraIDs({ includeDeleted: false }));
  }, [dispatch]);

  useEffect(() => {
    if (msEntraIDs && msEntraIDs.length) {
      setMsEntraIDOptions(msEntraIDs.map((s) => ({ label: s.name, value: s.id, description: s.description })));
    }
  }, [setMsEntraIDOptions, msEntraIDs]);

  // Ran on initialization (with making sure EosServices have a non-empty array)
  useEffect(() => {
    if (targetIdQuery && msEntraIDs && msEntraIDs.length) {
      void formik.setFieldValue(TARGET_ID, targetIdQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msEntraIDs]);

  const formInputIdPrefix = 'create-request-';
  return (
    <FormSection title={t('SELECT_SERVICE')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('MS_ENTRA_ID')}
            required
            description={t('MS_ENTRA_ID_INFO')}
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {<BarsTransition isLoading={loading} />}
            {!loading && msEntraIDs && (
              <FormSelectInput
                placeholder={t('MS_ENTRA_ID_PLACEHOLDER')}
                onChange={(option): void => {
                  void formik.setFieldValue(TARGET_ID, option.value);
                  void formik.setFieldValue(ACCESS_TYPE, '');
                  void formik.setFieldValue(ACCESS_TYPES, []);
                  setTargetIdQuery(option.value);
                  setAccessTypeQuery(undefined);
                }}
                value={msEntraIDOptions.find((option) => {
                  return option.value === field.value;
                })}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={msEntraIDOptions}
                aria-label={t('MS_ENTRA_ID')}
                formatOptionLabel={(
                  option: {
                    value: string;
                    label: string;
                    description: string;
                  },
                  state
                ) => {
                  const isSelected =
                    state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                  /* istanbul ignore next */
                  const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                  return (
                    <Stack direction="column" itemSpacing={0.5}>
                      <Box>{option.label}</Box>
                      <Tooltip content={option.description}>
                        <Box color={subtextColor} fontSize={11}>
                          {option.description}
                        </Box>
                      </Tooltip>
                    </Stack>
                  );
                }}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    marginTop: theme.space[1],
                    marginBottom: theme.space[1],
                    position: 'relative',
                    transform: 'none',
                    whiteSpace: 'normal',
                    maxHeight: '3.75em',
                  }),
                  input: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: theme.space[1] - 2,
                    paddingTop: 0,
                  }),
                }}
              />
            )}
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default MsEntraIDEnvironment;
